import { getPeriodBoundsFromYear } from '@/interfaces/Building.interface';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { NEVER, type Observable } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AdvizeoCoreBuildingsComparisonProvider } from '../../providers/buildings-comparison/advizeo-core-buildings-comparison.provider';
import { DashboardsProvider } from '../../providers/dashboards/dashboards.provider';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '../../providers/local-storage.contants';
import { DashboardsStoreActions } from '../entities/dashboards';
import { DashboardingStoreActions } from './index';

@Injectable({
    providedIn: 'root'
})
export class DashboardingStoreEffects {
    private _action$ = inject(Actions);
    private _advizeoCoreBuildingsComparisonProvider = inject(AdvizeoCoreBuildingsComparisonProvider);
    private _dashboardsProvider = inject(DashboardsProvider);

    setBuildingIdsEffect$: Observable<Action> = createEffect(() =>
        this._action$.pipe(
            ofType<DashboardingStoreActions.SetSelectedNodeId>(DashboardingStoreActions.ActionType.SET_SELECTED_NODE_ID),
            map(action => action.payload.selectedNodeId),
            switchMap(nodeId =>
                this._advizeoCoreBuildingsComparisonProvider.fetchComparisonPeriods$(nodeId).pipe(
                    map(periods => {
                        if (Array.isArray(periods)) {
                            const currentYear = Temporal.Now.plainDateISO().year;
                            periods = periods.filter(period => period <= currentYear);
                            return new DashboardingStoreActions.SetPeriods({
                                periods: periods.map(year => getPeriodBoundsFromYear(year)),
                                defaultPeriod: sessionStorage.getItem(SESSION_STORAGE_KEYS.SELECTED_PERIOD)
                            });
                        }
                    }),
                    filter(isNotNullish),
                    catchError(() => NEVER)
                )
            )
        )
    );

    setSelectedDashboardId$: Observable<Action> = createEffect(() =>
        this._action$.pipe(
            ofType<DashboardsStoreActions.DeleteDashboard>(DashboardsStoreActions.ActionType.DELETE_DASHBOARD),
            map(action => action.payload.dashboardId),
            switchMap(dashboardId =>
                this._dashboardsProvider
                    .getDashboardIds$()
                    .pipe(map(dashboardIds => (dashboardIds.length ? dashboardIds.filter(element => element !== dashboardId)[0] : null)))
            ),
            map(dashboardId => {
                return new DashboardingStoreActions.SetSelectedDashboardId({ selectedDashboardId: dashboardId });
            })
        )
    );

    SetIsEstimatedIgnoredOnWidgetsEffect$ = createEffect(
        () =>
            this._action$.pipe(
                ofType<DashboardingStoreActions.SetIsEstimatedIgnoredOnWidgets>(
                    DashboardingStoreActions.ActionType.SET_IS_ESTIMATED_IGNORED_ON_WIDGETS
                ),
                tap(action => {
                    localStorage.setItem(LOCAL_STORAGE_KEYS.IGNORE_ESTIMATED_ON_WIDGETS, String(action.payload));
                })
            ),
        { dispatch: false }
    );
}
